import { useState, useMemo, useCallback } from 'react'
import { router } from '@inertiajs/react'
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    TextField,
    CircularProgress,
    InputAdornment,
    Checkbox,
    FormControlLabel,
    keyframes
} from '@mui/material'
import {
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import CustomDatePicker from './CustomDatePicker'
import dogHuntingImage from '../assets/dog_hunting.png'
import GoogleSignInButton from './GoogleSignInButton'
import { useAuth } from '../hooks/useAuth'
import axiosClient from '../services/axiosClient'
import { trackFeatureUsage } from '../services/FeatureTrackingService'
import { GoogleAuthService } from '../services/GoogleAuthService'
import EditAvailability from './EditAvailability'
import PhoneInput from './PhoneInput'

const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;

interface TimeRange {
    startTime: string;
    endTime: string;
    hasConflict?: boolean;
}

interface DaySchedule {
    enabled: boolean;
    timeRanges: TimeRange[];
}

interface WeeklySchedule {
    [key: string]: DaySchedule;
}

interface RenterFormData {
    tourPreference: string;
    rentalType: string;
    additionalInfo: string;
    budget: string;
    employer: string;
    occupation: string;
    neighborhoods: string;
    numberOfApplicants: string;
    numDogs: string;
    numCats: string;
    moveInDate: string;
    bedrooms: string;
    bathrooms: string;
    leaseLength: string;
    housingVoucher: string;
    shortIntro: string;
    movingReason: string;
    phoneNumber: string;
    ignore: string;
    availability: WeeklySchedule;
    timezone: string;
}

interface Question {
    id: string;
    title: string;
    type: 'text' | 'long-text' | 'number' | 'checkbox' | 'single_choice' | 'date' | 'counter' | 'value-proposition' | 'generated_intro' | 'availability' | 'phone';
    field: keyof RenterFormData;
    options?: { value: string; label: string }[];
    placeholder?: string;
    subtext?: string;
    validation?: (value: any) => string | null;
}

const questions: Question[] = [
    {
        id: 'tourPreference',
        title: 'Your tour preference',
        type: 'single_choice',
        field: 'tourPreference',
        options: [
            { value: 'in_person', label: 'In person' },
            { value: 'virtual', label: 'Virtual' },
        ],
        validation: (value) => {
            if (!value) return 'Please select a tour preference';
            return null;
        }
    },
    {
        id: 'rentalType',
        title: 'What type of rental are you seeking?',
        type: 'single_choice',
        field: 'rentalType',
        options: [
            { value: 'entire_place', label: 'Entire Place' },
            { value: 'private_room', label: 'Private Room' },
        ],
        validation: (value) => {
            if (!value) return 'Please select a rental type';
            return null;
        }
    },

    {
        id: 'moveInDate',
        title: 'What move-in date are you seeking?',
        type: 'date',
        field: 'moveInDate',
        validation: (value) => {
            if (!value) return 'Move-in date is required';
            return null;
        }
    },
    {
        id: 'numberOfApplicants',
        title: 'Number of applicants?',
        type: 'counter',
        field: 'numberOfApplicants',
        validation: (value) => {
            if (!value || Number(value) < 1) return 'Please enter at least 1 person';
            return null;
        }
    },
    {
        id: 'bedrooms',
        title: 'How many bedrooms?',
        type: 'counter',
        field: 'bedrooms',
        validation: (value) => {
            if (!value || Number(value) < 0) return 'Please enter a valid number';
            return null;
        }
    },
    {
        id: 'bathrooms',
        title: 'How many bathrooms?',
        type: 'counter',
        field: 'bathrooms',
        validation: (value) => {
            if (!value || Number(value) < 1) return 'Please enter a valid number';
            return null;
        }
    },
    {
        id: 'numDogs',
        title: 'How many dogs do you have?',
        type: 'counter',
        field: 'numDogs',
        validation: (value) => {
            if (!value || Number(value) < 0) return 'Please enter a valid number';
            return null;
        }
    },
    {
        id: 'numCats',
        title: 'How many cats do you have?',
        type: 'counter',
        field: 'numCats',
        validation: (value) => {
            if (!value || Number(value) < 0) return 'Please enter a valid number';
            return null;
        }
    },
    {
        id: 'leaseLength',
        title: 'What lease length are you looking for?',
        type: 'single_choice',
        field: 'leaseLength',
        options: [
            { value: '12-month', label: '12 month lease' },
            { value: '6-month', label: '6 month lease' },
            { value: '3-month', label: '3 month lease' },
            { value: 'month-to-month', label: 'Month to Month' }
        ],
        validation: (value) => {
            if (!value) return 'Please select a lease length';
            return null;
        }
    },
    {
        id: 'neighborhoods',
        title: 'Which neighborhoods are you interested in?',
        type: 'text',
        field: 'neighborhoods',
        placeholder: 'Fells Point, Mount Vernon, Canton, Federal Hill, Bolton Hill, Penn North ...',
        subtext: 'New to the area? Just type "unsure" and we can help you decide',
        validation: (value) => {
            if (!value || value.trim() === '') return 'Please tell us which neighborhoods you\'re interested in';
            return null;
        }
    },
    {
        id: 'employer',
        title: 'Who is your employer?',
        subtext: 'Sharing this instantly makes you desirable.',
        type: 'text',
        field: 'employer',
        placeholder: 'Company name or organization',
        validation: (value) => {
            if (!value || value.trim() === '') return 'Employer is required';
            return null;
        }
    },
    {
        id: 'occupation',
        title: 'What is your job title?',
        subtext: 'Sharing this instantly makes you desirable.',
        type: 'text',
        field: 'occupation',
        placeholder: 'Job title or role',
        validation: (value) => {
            if (!value || value.trim() === '') return 'Occupation is required';
            return null;
        }
    },
    {
        id: 'additionalInfo',
        title: 'Anything else you need in your next home?',
        type: 'text',
        field: 'additionalInfo',
        placeholder: "Need  a backyard? Furnished home?",
        validation: (value) => {
            return null;
        }
    },
    {
        id: 'moving reason',
        title: 'Why are you moving?',
        type: 'text',
        field: 'movingReason',
        subtext: 'Sharing this helps landlords understand your situation.',
        validation: (value) => {
            if (!value || value.trim() === '') return 'Moving reason is required';
            return null;
        }
    },
    {
        id: 'budget',
        title: 'What is your budget?',
        type: 'number',
        field: 'budget',
        subtext: 'Only used for searching purposes and Never shared with landlords ',
        validation: (value) => {
            if (!value || value.trim() === '') return 'Budget is required';
            if (isNaN(Number(value))) return 'Budget must be a number';
            if (Number(value) < 0) return 'Budget must be positive';
            return null;
        }
    },

    {
        id: 'housingVoucher',
        title: 'Do you have a housing voucher?',
        type: 'single_choice',
        field: 'housingVoucher',
        subtext: 'We will tailor the search for landlords who accept vouchers',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
        ],
        validation: (value) => {
            if (!value) return 'Please select whether you have a housing voucher';
            return null;
        }
    },
    {
        id: 'shortIntro',
        title: 'Short intro about yourself',
        type: 'long-text',
        field: 'shortIntro',
        subtext: 'A warm intro is a great way to break the ice with landlords, it goes a longway',
        placeholder: "Hi there! I am 25 years old nurse working at Hopkins. I will be moving with my dog Charles, he's a 2 year old Golden Doodle. I love running, hikiing and cooking",
        validation: (value) => {
            if (!value || value.trim() === '') return 'Please tell us a bit about yourself';
            return null;
        }
    },

    {
        id: 'availability',
        title: 'Set your tour availability',
        type: 'availability',
        field: 'availability',
        subtext: 'Let landlords know when you\'re available for tours',
        validation: (value) => {
            const schedule = value as WeeklySchedule;
            const hasEnabledDay = Object.values(schedule).some(day => day.enabled);
            if (!hasEnabledDay) {
                return 'Please enable at least one day for tour availability';
            }
            return null;
        }
    },
    {
        id: 'phoneNumber',
        title: 'What is your phone number?',
        type: 'phone',
        field: 'phoneNumber',
        placeholder: '(555) 555 - 5555',
        subtext: 'Only used to send you notifications',
        validation: (value) => {
            if (!value || value.trim() === '') return 'Phone number is required';
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.length < 10) return 'Please enter a valid phone number';
            return null;
        }
    },

    {
        id: 'signin',
        title: 'Almost there, let us work our magic',
        type: 'value-proposition',
        field: 'ignore',
        validation: (value) => {
            return null;
        }
    },
];

// SingleChoiceQuestion Component
const SingleChoiceQuestion = ({
    options,
    value,
    onSelect
}: {
    options: { value: string; label: string }[];
    value: string;
    onSelect: (value: string) => void;
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {options.map(option => (
                <Button
                    key={option.value}
                    variant={value === option.value ? 'contained' : 'outlined'}
                    onClick={() => onSelect(option.value)}
                    sx={{
                        width: '98%',
                        padding: '1rem',
                        textTransform: 'none',
                        justifyContent: 'center',
                        backgroundColor: value === option.value ? '#F5F5F5' : 'transparent',
                        color: '#000',
                        border: '2px solid #E5E5E5',
                        boxShadow: 'none',
                        fontSize: '1rem',
                        borderColor: value === option.value ? '#000' : '#E5E5E5',
                        '&:hover': {
                            borderColor: '#000',
                            backgroundColor: value === option.value ? '#F5F5F5' : 'transparent',
                            color: '#000',
                            boxShadow: 'none',
                        },
                    }}
                >
                    {option.label}
                </Button>
            ))}
        </Box>
    );
};

// CheckboxQuestion Component
const CheckboxQuestion = ({
    options,
    value,
    onSelect
}: {
    options: { value: string; label: string }[];
    value: string[];
    onSelect: (values: string[]) => void;
}) => {
    const handleToggle = (optionValue: string) => {
        const currentValue = value || [];
        const newValue = currentValue.includes(optionValue)
            ? currentValue.filter(v => v !== optionValue)
            : [...currentValue, optionValue];
        onSelect(newValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {options.map(option => (
                <FormControlLabel
                    key={option.value}
                    control={
                        <Checkbox
                            checked={value?.includes(option.value) || false}
                            onChange={() => handleToggle(option.value)}
                            sx={{
                                color: '#E5E5E5',
                                '&.Mui-checked': {
                                    color: '#000',
                                },
                            }}
                        />
                    }
                    label={option.label}
                    sx={{
                        margin: 0,
                        padding: '0.75rem',
                        border: '1px solid #E5E5E5',
                        borderRadius: '4px',
                        backgroundColor: value?.includes(option.value) ? '#F5F5F5' : 'transparent',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                        },
                        '& .MuiFormControlLabel-label': {
                            fontSize: '1rem',
                            color: '#000',
                        },
                    }}
                />
            ))}
        </Box>
    );
};

// GeneratedIntroQuestion Component
const GeneratedIntroQuestion = ({
    backgroundIntro,
    onCopyIntro,
    copiedIntro,
    currentIntro,
    onIntroChange
}: {
    backgroundIntro: string | null;
    onCopyIntro: () => void;
    copiedIntro: boolean;
    currentIntro: string;
    onIntroChange: (value: string) => void;
}) => {
    if (!backgroundIntro) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box
                    component="img"
                    src={dogHuntingImage}
                    alt="Dog Hunting"
                    sx={{
                        display: 'block',
                        animation: `${waveAnimation} 2s ease-in-out infinite`,
                        margin: '0 auto',
                        width: '120px',
                        marginBottom: '2rem'
                    }}
                />
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Crafting your perfect intro...
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                    Our AI is writing a personalized introduction for you.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="caption" sx={{ mb: 2 }}>Feel free to edit your intro</Typography>
            <Box>
                <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={currentIntro || backgroundIntro}
                    onChange={(e) => onIntroChange(e.target.value)}
                    variant="outlined"
                    placeholder="Enter your intro"
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onCopyIntro}
                        sx={{
                            borderColor: copiedIntro ? 'success.main' : 'grey.400',
                            color: copiedIntro ? 'success.main' : 'grey.700',
                            '&:hover': {
                                borderColor: copiedIntro ? 'success.main' : 'grey.500',
                                bgcolor: copiedIntro ? 'success.50' : 'grey.100'
                            }
                        }}
                    >
                        {copiedIntro ? 'Copied!' : 'Copy Intro'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

interface RenterOnboardingFormProps {
    isVisible: boolean;
    id?: string;
}

export default function RenterOnboardingForm({ isVisible, id }: RenterOnboardingFormProps) {
    const { renter } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generatedIntro, setGeneratedIntro] = useState<string | null>(null);
    const [backgroundGeneratedIntro, setBackgroundGeneratedIntro] = useState<string | null>(null);
    const [copiedIntro, setCopiedIntro] = useState(false);
    const [backgroundRequestsTriggered, setBackgroundRequestsTriggered] = useState<{
        introGeneration: boolean;
    }>({
        introGeneration: false
    });

    const [formData, setFormData] = useState<RenterFormData>({
        tourPreference: '',
        rentalType: '',
        additionalInfo: '',
        budget: '',
        employer: '',
        occupation: '',
        neighborhoods: '',
        numberOfApplicants: '1',
        numDogs: '0',
        numCats: '0',
        moveInDate: format(new Date(), 'yyyy-MM-dd'),
        bedrooms: '0',
        bathrooms: '1',
        leaseLength: '',
        housingVoucher: '',
        shortIntro: '',
        ignore: '',
        movingReason: '',
        phoneNumber: '',
        availability: {
            'SUN': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
            'MON': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
            'TUE': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
            'WED': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
            'THU': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
            'FRI': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] },
            'SAT': { enabled: false, timeRanges: [{ startTime: '13:00', endTime: '14:00' }] }
        },
        timezone: 'America/New_York'
    });

    const currentQuestion = questions[currentStep];

    // Memoize date value to prevent DatePicker from resetting
    const memoizedDateValue = useMemo(() => {
        if (!currentQuestion || currentQuestion.type !== 'date') return null;
        const value = formData[currentQuestion.field];
        return value ? new Date(`${value}T00:00:00`) : null;
    }, [currentQuestion, formData]);

    const handleInputChange = (field: keyof RenterFormData, value: string | string[] | WeeklySchedule) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateCurrentStep = () => {
        if (!currentQuestion) return true;

        const validation = currentQuestion.validation;
        if (!validation) return true;

        const error = validation(formData[currentQuestion.field]);
        if (error) {
            setErrors(prev => ({ ...prev, [currentQuestion.field]: error }));
            return false;
        }

        return true;
    };

    const handleNext = () => {
        if (validateCurrentStep()) {
            if (currentStep < questions.length - 1) {
                const nextStep = currentStep + 1;
                setCurrentStep(nextStep);

                // Trigger background request after occupation question is completed
                const currentQuestionId = questions[currentStep]?.id;
                if (currentQuestionId === 'occupation' && !backgroundRequestsTriggered.introGeneration) {
                    setBackgroundRequestsTriggered(prev => ({ ...prev, introGeneration: true }));
                    // Trigger in background without blocking
                    setTimeout(() => {
                        generateRenterIntro();
                    }, 100);
                }

                // Copy background content to display content when reaching generated intro step
                if (questions[nextStep]?.type === 'generated_intro' && backgroundGeneratedIntro) {
                    setGeneratedIntro(backgroundGeneratedIntro);
                }
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleGoogleSignIn = useCallback(async (response: any) => {
        if (response.credential) {
            console.log("Response Credential is:", response.credential);
            try {
                await GoogleAuthService.handleGoogleResponseForRenter(response, () => {
                    console.log('Google sign-in successful');
                    router.reload();
                });
                console.log('Google sign-in successful');
            } catch (error) {
                console.error('Google sign-in failed:', error);
            }
        }
    }, []);

    const handleGoToDashboard = async () => {
        // Navigate to dashboard or user profile
        console.log("Form data is:", formData);
        console.log('Navigate to dashboard');
        try {
            setIsSubmitting(true);
            console.log('Renter form data:', {
                ...formData,
            });

            // Make API call to create profile
            const profileResponse = await axiosClient.post(`/renters/${renter?.id}/create_profile`, {
                ...formData,
            });

            console.log('Profile created successfully:', profileResponse.data);

            // Clean up the availability schedule by removing time ranges for disabled days
            const cleanedSchedule = Object.entries(formData.availability).reduce((acc, [dayId, daySchedule]) => {
                if (daySchedule.enabled) {
                    acc[dayId] = daySchedule;
                } else {
                    acc[dayId] = { enabled: false, timeRanges: [] };
                }
                return acc;
            }, {} as WeeklySchedule);

            // Make API call to save tour availability
            const availabilityResponse = await axiosClient.post(`/renters/${renter?.id}/tour_availability`, {
                tour_availability: {
                    schedule: cleanedSchedule,
                    timezone: formData.timezone
                }
            });

            console.log('Tour availability saved successfully:', availabilityResponse.data);

        } catch (error) {
            console.error('Error creating profile or saving availability:', error);
            // You could add error handling here, like showing a toast notification
        } finally {
            setIsSubmitting(false);
        }
        // You can add your navigation logic here

        router.visit(`/renters/home`);
    };

    const handleCopyIntro = async () => {
        try {
            const intro = (generatedIntro || backgroundGeneratedIntro)?.trim() || '';
            if (!intro) return;
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(intro);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = intro;
                textarea.style.position = 'fixed';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopiedIntro(true);
            trackFeatureUsage(renter?.id ?? null, 'Copy Intro');
            setTimeout(() => setCopiedIntro(false), 2000);
        } catch (_) { }
    };

    const generateRenterIntro = async () => {
        try {
            const requestData = {
                neighborhoods: formData.neighborhoods,
                moveInDate: formData.moveInDate,
                leaseLength: formData.leaseLength,
                additionalInfo: formData.additionalInfo,
                employer: formData.employer,
                occupation: formData.occupation,
                numberOfApplicants: formData.numberOfApplicants,
                numDogs: formData.numDogs,
                numCats: formData.numCats
            };

            const response = await axiosClient.post('/api/v1/open_ai_api/generate_renter_intro', requestData);
            const data = response.data;

            setBackgroundGeneratedIntro(data.intro);
            // Also update the form data so user can edit it
            setFormData(prev => ({
                ...prev,
                shortIntro: data.intro
            }));

        } catch (error) {
            console.error('Error generating renter intro:', error);
            // Fallback to basic intro if API fails
            const fallbackIntro = `Hi! I'm a ${formData.occupation} working at ${formData.employer}. I'm looking for a place in ${formData.neighborhoods} to move in on ${formData.moveInDate}. I have ${formData.numDogs} dogs and ${formData.numCats} cats.}`;

            setBackgroundGeneratedIntro(fallbackIntro);
            setFormData(prev => ({
                ...prev,
                shortIntro: fallbackIntro
            }));
        }
    };

    const renderQuestion = () => {
        if (!currentQuestion) return null;

        const { type, field, options, placeholder } = currentQuestion;
        const value = formData[field];
        const error = errors[field];

        switch (type) {
            case 'text':
                return (
                    <TextField
                        multiline
                        rows={1}
                        fullWidth
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        error={!!error}
                        helperText={error}
                        placeholder={placeholder}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: 2,
                                '& fieldset': {
                                    borderColor: 'grey.300',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey.400',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#fbbf24',
                                },
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '1rem',
                                lineHeight: 1.5,
                                color: 'grey.900',
                                '&::placeholder': {
                                    color: 'grey.500',
                                    opacity: 1,
                                },
                            },
                        }}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }
                        }}
                    />
                );

            case 'long-text':
                return (
                    <TextField
                        multiline
                        rows={8}
                        fullWidth
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        error={!!error}
                        helperText={error}
                        placeholder={placeholder}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: 2,
                                '& fieldset': {
                                    borderColor: 'grey.300',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey.400',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#fbbf24',
                                },
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '1rem',
                                lineHeight: 1.5,
                                color: 'grey.900',
                                '&::placeholder': {
                                    color: 'grey.500',
                                    opacity: 1,
                                },
                            },
                        }}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }
                        }}
                    />
                );

            case 'phone':
                return (
                    <PhoneInput
                        value={typeof value === 'string' ? value : ''}
                        onChange={(digits) => handleInputChange(field, digits)}
                        error={!!error}
                        helperText={error}
                        placeholder={placeholder}
                    />
                );

            case 'number':
                return (
                    <TextField
                        fullWidth
                        type="number"
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        error={!!error}
                        helperText={error}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder={placeholder}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }
                        }}
                    />
                );

            case 'date':
                return (
                    <CustomDatePicker
                        value={memoizedDateValue}
                        onChange={(newValue) => {
                            const formatted = newValue ? format(newValue, 'yyyy-MM-dd') : '';
                            handleInputChange(field, formatted);
                        }}
                        error={!!error}
                        helperText={error}
                    />
                );

            case 'single_choice':
                return (
                    <SingleChoiceQuestion
                        options={options || []}
                        value={typeof value === 'string' ? value : ''}
                        onSelect={(selectedValue) => {
                            handleInputChange(field, selectedValue);
                            // Auto-advance to next question after state update
                            setTimeout(() => {
                                if (currentStep < questions.length - 1) {
                                    setCurrentStep(currentStep + 1);
                                }
                            }, 50);
                        }}
                    />
                );

            case 'checkbox':
                return (
                    <CheckboxQuestion
                        options={options || []}
                        value={Array.isArray(value) ? value : []}
                        onSelect={(selectedValues) => {
                            handleInputChange(field, selectedValues);
                        }}
                    />
                );

            case 'counter':
                const minValue = field === 'numberOfApplicants' ? 1 : 0;
                const currentValue = Number(typeof value === 'string' ? value : String(minValue)) || minValue;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            onClick={() => {
                                const newValue = Math.max(minValue, currentValue - 1);
                                handleInputChange(field, String(newValue));
                            }}
                            disabled={currentValue <= minValue}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                p: 0,
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                color: 'black',
                                mx: 2
                            }}
                        >
                            <RemoveIcon />
                        </Button>
                        <TextField
                            value={typeof value === 'string' ? value : String(minValue)}
                            onChange={(e) => handleInputChange(field, e.target.value)}
                            sx={{
                                width: '70px',
                                '& input': { textAlign: 'center' }
                            }}
                            InputProps={{
                                inputProps: { min: minValue },
                            }}
                        />
                        <Button
                            onClick={() => {
                                handleInputChange(field, String(currentValue + 1));
                            }}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                p: 0,
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                color: 'black',
                                mx: 2
                            }}
                        >
                            <AddIcon />
                        </Button>
                    </Box>
                );

            case 'generated_intro':
                return (
                    <GeneratedIntroQuestion
                        backgroundIntro={backgroundGeneratedIntro}
                        onCopyIntro={handleCopyIntro}
                        copiedIntro={copiedIntro}
                        currentIntro={formData.shortIntro || ''}
                        onIntroChange={(value) => handleInputChange('shortIntro', value)}
                    />
                );

            case 'availability':
                return (
                    <EditAvailability
                        schedule={value as WeeklySchedule}
                        timezone={formData.timezone}
                        onChange={(newSchedule, newTimezone) => {
                            handleInputChange(field, newSchedule);
                            handleInputChange('timezone', newTimezone);
                        }}
                    />
                );

            case 'value-proposition':
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mb: 2,
                        alignItems: 'center'
                    }}>
                        {/* Dog hunting image on top */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            maxWidth: '300px',
                            width: { xs: '70%', md: '80%' }
                        }}>
                            <img
                                src={dogHuntingImage}
                                alt="Dog Hunting"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>

                        {/* Message below */}
                        <Box sx={{
                            width: '100%'
                        }}>
                            <Card sx={{
                                p: 2,
                                backgroundColor: 'grey.50',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 2,
                                fontFamily: 'Inter, sans-serif'
                            }}>
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    mb: 2,
                                    color: 'grey.900',
                                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                                    textAlign: 'left'
                                }}>
                                   { renter ? 'Your dashboard is ready!' : 'Amazing awaits! 🎉' }
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        color: 'grey.700',
                                        lineHeight: 1.5
                                    }}>
                                        <Box sx={{ fontSize: '1.2em', lineHeight: 1 }}>🎯</Box>
                                        <Box>{ renter ? 'View your dashboard' : 'Sign in' }</Box>
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        color: 'grey.700',
                                        lineHeight: 1.5
                                    }}>
                                        <Box sx={{ fontSize: '1.2em', lineHeight: 1 }}>🐕</Box>
                                        <Box>We share your profile and find matches</Box>
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        color: 'grey.700',
                                        lineHeight: 1.5
                                    }}>
                                        <Box sx={{ fontSize: '1.2em', lineHeight: 1 }}>✅</Box>
                                        <Box>Wake up to new tour requests that you accept or reject</Box>
                                    </Box>
                                </Box>
                            </Card>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <Box
            id={id}
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'grey.50',
                borderTop: '1px solid #e5e7eb'
            }}
        >
            <Container maxWidth="md">
                <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        align="center"
                        sx={{
                            fontWeight: 700,
                            mt: 3,
                            mb: 1,
                            fontSize: { xs: '1.875rem', sm: '2.25rem' },
                            color: 'grey.900'
                        }}
                    >
                        Let's find your next home! 🏠
                    </Typography>
                    <Typography
                        variant="body1"
                        align="center"
                        sx={{
                            mb: 4,
                            color: 'grey.600',
                            fontSize: '1.125rem'
                        }}
                    >
                        Tell us a bit more about what you're looking for.
                    </Typography>

                    <Card
                        elevation={2}
                        sx={{
                            borderRadius: 3,
                            p: { xs: 2, sm: 3 },
                            border: '1px solid #e5e7eb'
                        }}
                    >
                        {/* Progress indicator */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ color: 'grey.600', mb: 1 }}>
                                Question {currentStep + 1} of {questions.length}
                            </Typography>
                            <Box sx={{
                                width: '100%',
                                height: 4,
                                bgcolor: 'grey.200',
                                borderRadius: 2,
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    width: `${((currentStep + 1) / questions.length) * 100}%`,
                                    height: '100%',
                                    bgcolor: '#fbbf24',
                                    transition: 'width 0.3s ease'
                                }} />
                            </Box>
                        </Box>

                        {/* Question */}
                        {currentQuestion && (
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 600,
                                        mb: currentQuestion.subtext ? 1 : 3,
                                        color: 'grey.900'
                                    }}
                                >
                                    {currentQuestion.title}
                                </Typography>
                                {currentQuestion.subtext && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'grey.600',
                                            mb: 3,
                                            fontSize: '0.875rem',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        {currentQuestion.subtext}
                                    </Typography>
                                )}
                                {renderQuestion()}
                            </Box>
                        )}

                        {/* Navigation buttons */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 4,
                            gap: { xs: 3, md: 6 }
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBack}
                                disabled={currentStep === 0}
                                sx={{
                                    borderColor: 'grey.300',
                                    color: 'grey.700',
                                    '&:hover': {
                                        borderColor: 'grey.400',
                                        bgcolor: 'grey.50'
                                    }
                                }}
                            >
                                Back
                            </Button>

                            {/* Only show Next button for non-single-choice questions */}
                            {currentQuestion?.type !== 'single_choice' && (
                                currentQuestion?.type === 'value-proposition' ? (
                                    !renter ? (
                                        <GoogleSignInButton googleCallback={handleGoogleSignIn} />
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={handleGoToDashboard}
                                            endIcon={
                                                <ArrowForwardIcon sx={{ color: '#fbbf24' }} />
                                            }
                                            sx={{
                                                bgcolor: 'black',
                                                color: 'white',
                                                px: 3,
                                                '&:hover': {
                                                    bgcolor: 'grey.900',
                                                    color: 'white'
                                                }
                                            }}
                                        >
                                            Dashboard
                                        </Button>
                                    )
                                ) : (
                                    <Button
                                        variant="contained"
                                        endIcon={
                                            currentStep === questions.length - 1 ?
                                                <CheckCircleIcon sx={{ color: '#fbbf24' }} /> :
                                                <ArrowForwardIcon sx={{ color: '#fbbf24' }} />
                                        }
                                        onClick={handleNext}
                                        disabled={isSubmitting}
                                        sx={{
                                            bgcolor: 'black',
                                            color: 'white',
                                            px: 3,
                                            '&:hover': {
                                                bgcolor: 'grey.900',
                                                color: 'white'
                                            },
                                            '&:disabled': {
                                                bgcolor: 'grey.400',
                                                color: 'grey.600'
                                            }
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                                Submitting...
                                            </>
                                        ) : currentStep === questions.length - 1 ? (
                                            'Find My Home'
                                        ) : (
                                            'Next'
                                        )}
                                    </Button>
                                )
                            )}
                        </Box>
                    </Card>
                </Box>
            </Container>
        </Box>
    );
}
